import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import ApiService from "../../_services/ApiService";

// ── Types ──────────────────────────────────────────────────────────────────

export type IssueRecord = {
  id: number;
  batchId: number | null;
  batchName: string | null;
  batchDisplayName: string | null;
  batchCode: string | null;
  schoolId: number | null;
  schoolName: string | null;
  courseId: number | null;
  courseName: string | null;
  programId: number | null;
  programName: string | null;
  yearId: number | null;
  yearName: string | null;
  startDate: string | null;
  endDate: string | null;
  creationDate: string;
};

type SchoolOption = { schoolId: number; schoolName: string };
type YearOption = { yearId: number; yearName: string };
type ProgramOption = { programId: number; programName: string };
type CourseOption = { courseId: number; courseName: string };
type BatchOption = { id: number; name: string; batchCode: string; displayName: string };

export type IssuePayload = {
  batchIds: number[];
  startDate?: string;
  endDate?: string;
  schoolId?: number;
  yearId?: number;
  programId?: number;
  courseId?: number;
  courseIds?: number[];
  notifySms?: boolean;
  notifyEmail?: boolean;
  notifyApp?: boolean;
  smsMessage?: string;
  emailMessage?: string;
  appMessage?: string;
};

export type IssueFormProps = {
  resourceId: number;
  isViewMode?: boolean;
  onIssue: (payload: IssuePayload) => Promise<any>;
  onGetHistory: () => Promise<any>;
  onDelete: (issueId: number) => Promise<any>;
};

// ── Component ──────────────────────────────────────────────────────────────

export default function IssueForm({ resourceId, isViewMode = false, onIssue, onGetHistory, onDelete }: IssueFormProps) {
  // Cascading dropdown data
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [years, setYears] = useState<YearOption[]>([]);
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [filteredBatches, setFilteredBatches] = useState<BatchOption[]>([]);

  // Selected values
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | "">("");
  const [selectedYearId, setSelectedYearId] = useState<number | "">("");
  const [selectedProgramId, setSelectedProgramId] = useState<number | "">("");
  const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState<number[]>([]);

  // Dates — default: today → 1 year from today
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });

  // Notifications
  const [notifySms, setNotifySms] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyApp, setNotifyApp] = useState(false);
  const [smsMessage, setSmsMessage] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [appMessage, setAppMessage] = useState("");

  // Issue history
  const [issueHistory, setIssueHistory] = useState<IssueRecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // UI states
  const [issuing, setIssuing] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showBatchOverride, setShowBatchOverride] = useState(false);

  // System config
  const [isProgramBased, setIsProgramBased] = useState<boolean | null>(null);

  // Loading states for cascading
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);

  // Track batch fetch version to avoid stale results
  const batchFetchVersion = useRef(0);

  // Load schools + years + system config + history on mount
  useEffect(() => {
    if (!resourceId) return;

    ApiService.getScheduleSystemConfig()
      .then((res) => {
        if (res.data?.success) setIsProgramBased(res.data.data.isProgramBased);
      })
      .catch(() => setIsProgramBased(false));

    setLoadingSchools(true);
    ApiService.getAcademicSchools()
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setSchools(Array.isArray(data) ? data : []);
      })
      .catch(() => setSchools([]))
      .finally(() => setLoadingSchools(false));

    setLoadingYears(true);
    ApiService.getAcademicYears()
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setYears(Array.isArray(data) ? data : []);
      })
      .catch(() => setYears([]))
      .finally(() => setLoadingYears(false));

    loadHistory();
  }, [resourceId]);

  // When year changes → fetch programs, reset downstream
  useEffect(() => {
    setPrograms([]);
    setCourses([]);
    setFilteredBatches([]);
    setSelectedProgramId("");
    setSelectedCourseIds([]);
    setSelectedBatchIds([]);

    if (!selectedYearId) return;

    setLoadingPrograms(true);
    ApiService.getAcademicPrograms(String(selectedYearId))
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setPrograms(Array.isArray(data) ? data : []);
      })
      .catch(() => setPrograms([]))
      .finally(() => setLoadingPrograms(false));
  }, [selectedYearId]);

  // When year/program changes → fetch courses.
  // Year is optional: when "All" (no year), fetch ALL courses across years.
  useEffect(() => {
    setCourses([]);
    setSelectedCourseIds([]);

    setLoadingCourses(true);
    ApiService.getAcademicCourses(
      selectedYearId ? Number(selectedYearId) : undefined,
      selectedProgramId ? Number(selectedProgramId) : undefined
    )
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setCourses(Array.isArray(data) ? data : []);
      })
      .catch(() => setCourses([]))
      .finally(() => setLoadingCourses(false));
  }, [selectedYearId, selectedProgramId]);

  // When selected courses change → fetch batches (union across all selected courses)
  useEffect(() => {
    setFilteredBatches([]);
    setSelectedBatchIds([]);
    setShowBatchOverride(false);

    const hasFilters = selectedCourseIds.length > 0 || !!selectedProgramId || !!selectedYearId || !!selectedSchoolId;
    if (!hasFilters) return;

    // If no courses selected, fall back to program/year/school-level batch fetch
    const courseIdsToFetch = selectedCourseIds.length > 0
      ? selectedCourseIds
      : [undefined]; // single call without courseId

    const version = ++batchFetchVersion.current;
    setLoadingBatches(true);

    Promise.all(
      courseIdsToFetch.map((cId) => {
        const filters: { schoolId?: number; courseId?: number; programId?: number; yearId?: number } = {};
        if (selectedSchoolId) filters.schoolId = Number(selectedSchoolId);
        if (cId) filters.courseId = cId;
        if (selectedProgramId) filters.programId = Number(selectedProgramId);
        if (selectedYearId) filters.yearId = Number(selectedYearId);
        if (!filters.schoolId && !filters.courseId && !filters.programId && !filters.yearId) return Promise.resolve([]);
        return ApiService.getBatchesByFilters(filters)
          .then((res) => {
            const data = res.data?.data || res.data || [];
            return Array.isArray(data) ? data : [];
          })
          .catch(() => [] as BatchOption[]);
      })
    ).then((results) => {
      if (version !== batchFetchVersion.current) return; // stale
      // Merge and dedupe by batch id
      const seen = new Set<number>();
      const merged: BatchOption[] = [];
      for (const list of results) {
        for (const b of list) {
          if (!seen.has(b.id)) {
            seen.add(b.id);
            merged.push(b);
          }
        }
      }
      merged.sort((a, b) => (a.displayName || a.name).localeCompare(b.displayName || b.name));
      setFilteredBatches(merged);
    }).finally(() => {
      if (version === batchFetchVersion.current) setLoadingBatches(false);
    });
  }, [selectedCourseIds, selectedProgramId, selectedYearId, selectedSchoolId]);

  const loadHistory = useCallback(async () => {
    if (!resourceId) return;
    setLoadingHistory(true);
    try {
      const res = await onGetHistory();
      const data = res.data?.data || res.data || [];
      setIssueHistory(Array.isArray(data) ? data : []);
    } catch {
      setIssueHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [resourceId, onGetHistory]);

  // ── Course multi-select helpers ──

  const toggleCourse = (courseId: number) => {
    setSelectedCourseIds((prev) =>
      prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId]
    );
  };

  const handleSelectAllCourses = () => {
    if (selectedCourseIds.length === courses.length) {
      setSelectedCourseIds([]);
    } else {
      setSelectedCourseIds(courses.map((c) => c.courseId));
    }
  };

  // ── Batch multi-select helpers ──

  const handleSelectAllBatches = () => {
    if (selectedBatchIds.length === filteredBatches.length) {
      setSelectedBatchIds([]);
    } else {
      setSelectedBatchIds(filteredBatches.map((b) => b.id));
    }
  };

  const toggleBatch = (batchId: number) => {
    setSelectedBatchIds((prev) =>
      prev.includes(batchId) ? prev.filter((id) => id !== batchId) : [...prev, batchId]
    );
  };

  // ── Issue logic ──

  const hasLevelSelected = isProgramBased
    ? !!selectedProgramId
    : selectedCourseIds.length > 0;

  // School + dates are mandatory; year/program/course/batch are optional.
  // With only School + dates set, the resource is issued to the entire school.
  const canIssue = !!selectedSchoolId && !!startDate && !!endDate;

  const getIssueLabel = () => {
    if (issuing) return "Issuing...";
    if (selectedBatchIds.length > 0)
      return `Issue to ${selectedBatchIds.length} Batch${selectedBatchIds.length !== 1 ? "es" : ""}`;
    if (hasLevelSelected && !showBatchOverride) {
      if (isProgramBased) {
        const p = programs.find((x) => x.programId === Number(selectedProgramId));
        return `Issue to ${p?.programName || "program"}`;
      }
      if (selectedCourseIds.length === 1) {
        const c = courses.find((x) => x.courseId === selectedCourseIds[0]);
        return `Issue to ${c?.courseName || "course"}`;
      }
      return `Issue to ${selectedCourseIds.length} Courses`;
    }
    // School-level issue (no course/program/batch) — across all years if Year = All
    const school = schools.find((x) => x.schoolId === Number(selectedSchoolId));
    return `Issue to ${school?.schoolName || "school"}${selectedYearId ? "" : " (all years)"}`;
  };

  const handleIssue = async () => {
    if (!resourceId || !canIssue) return;

    if (!selectedSchoolId) {
      setMessage({ type: "error", text: "School is required" });
      return;
    }
    if (!startDate) {
      setMessage({ type: "error", text: "Visible From date is required" });
      return;
    }
    if (!endDate) {
      setMessage({ type: "error", text: "Visible To date is required" });
      return;
    }

    setIssuing(true);
    setMessage(null);
    try {
      const res = await onIssue({
        batchIds: selectedBatchIds.length > 0 ? selectedBatchIds : [],
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        schoolId: selectedSchoolId ? Number(selectedSchoolId) : undefined,
        yearId: selectedYearId ? Number(selectedYearId) : undefined,
        programId: selectedProgramId ? Number(selectedProgramId) : undefined,
        // Send courseIds array for multi-select; also send courseId for backward compat
        courseIds: selectedCourseIds.length > 0 ? selectedCourseIds : undefined,
        courseId: selectedCourseIds.length === 1 ? selectedCourseIds[0] : undefined,
        notifySms: notifySms || undefined,
        notifyEmail: notifyEmail || undefined,
        notifyApp: notifyApp || undefined,
        smsMessage: notifySms && smsMessage ? smsMessage : undefined,
        emailMessage: notifyEmail && emailMessage ? emailMessage : undefined,
        appMessage: notifyApp && appMessage ? appMessage : undefined,
      });

      const result = res.data;
      if (result?.status === 1) {
        setMessage({ type: "success", text: result.message || "Issued successfully" });
        setSelectedBatchIds([]);
        loadHistory();
      } else {
        setMessage({ type: "error", text: result?.message || "Failed to issue" });
      }
    } catch (err: any) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to issue" });
    } finally {
      setIssuing(false);
    }
  };

  const handleDelete = async () => {
    if (!resourceId || !deleteConfirmId) return;
    setDeleting(true);
    try {
      await onDelete(deleteConfirmId);
      setIssueHistory((prev) => prev.filter((r) => r.id !== deleteConfirmId));
      setMessage({ type: "success", text: "Issue deleted successfully" });
    } catch (err: any) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to delete issue" });
    } finally {
      setDeleteConfirmId(null);
      setDeleting(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const levelLabel = isProgramBased ? "program" : "course(s)";

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Issue Form Card */}
      <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <span>Issue</span>
        </div>
        <div style={{ padding: 20 }}>
          {message && (
            <div
              style={{
                padding: "10px 14px",
                marginBottom: 16,
                borderRadius: 6,
                backgroundColor: message.type === "success" ? "#ecfdf5" : "#fef2f2",
                color: message.type === "success" ? "#065f46" : "#991b1b",
                border: `1px solid ${message.type === "success" ? "#a7f3d0" : "#fecaca"}`,
              }}
            >
              {message.text}
              <button
                onClick={() => setMessage(null)}
                style={{ float: "right", background: "none", border: "none", cursor: "pointer", fontWeight: "bold" }}
              >
                x
              </button>
            </div>
          )}

          {/* Cascading Dropdowns — 2-column grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            {/* School */}
            <div>
              <label style={labelStyle}>
                School <span style={{ color: "var(--status-danger)" }}>*</span>
              </label>
              <select
                value={selectedSchoolId}
                onChange={(e) => setSelectedSchoolId(e.target.value ? Number(e.target.value) : "")}
                style={selectStyle}
                disabled={isViewMode}
              >
                <option value="">-- Select School --</option>
                {schools.map((s) => (
                  <option key={s.schoolId} value={s.schoolId}>
                    {s.schoolName}
                  </option>
                ))}
              </select>
              {loadingSchools && <span style={loadingHint}>Loading...</span>}
            </div>

            {/* Year */}
            <div>
              <label style={labelStyle}>Year</label>
              <select
                value={selectedYearId}
                onChange={(e) => setSelectedYearId(e.target.value ? Number(e.target.value) : "")}
                style={selectStyle}
                disabled={isViewMode}
              >
                <option value="">All</option>
                {years.map((y) => (
                  <option key={y.yearId} value={y.yearId}>
                    {y.yearName}
                  </option>
                ))}
              </select>
              {loadingYears && <span style={loadingHint}>Loading...</span>}
            </div>

            {/* Program — shown for program-based systems */}
            {isProgramBased !== false && (
              <div>
                <label style={labelStyle}>Program</label>
                <select
                  value={selectedProgramId}
                  onChange={(e) => setSelectedProgramId(e.target.value ? Number(e.target.value) : "")}
                  style={selectStyle}
                  disabled={isViewMode || !selectedYearId}
                >
                  <option value="">{selectedYearId ? "-- Select Program --" : "Select Year first"}</option>
                  {programs.map((p) => (
                    <option key={p.programId} value={p.programId}>
                      {p.programName}
                    </option>
                  ))}
                </select>
                {loadingPrograms && <span style={loadingHint}>Loading...</span>}
              </div>
            )}

            {/* Course — multi-select checkbox list */}
            <div>
              <label style={labelStyle}>
                Course{" "}
                {selectedCourseIds.length > 0 && (
                  <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>({selectedCourseIds.length} selected)</span>
                )}
              </label>
              {loadingCourses ? (
                <div style={{ padding: 8, color: "var(--text-muted)", fontSize: 13 }}>Loading...</div>
              ) : courses.length === 0 ? (
                <div style={{ padding: "8px 10px", fontSize: 13, color: "var(--text-faint)", border: "1px solid var(--border-strong)", borderRadius: 6, background: "var(--surface-page)" }}>
                  No courses found
                </div>
              ) : (
                <div style={{ border: "1px solid var(--border-strong)", borderRadius: 6, maxHeight: 160, overflowY: "auto", padding: 8 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontWeight: 600, fontSize: 13, borderBottom: "1px solid var(--border-default)", marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      checked={selectedCourseIds.length === courses.length && courses.length > 0}
                      onChange={handleSelectAllCourses}
                      disabled={isViewMode}
                    />
                    Select All ({courses.length})
                  </label>
                  {courses.map((c) => (
                    <label key={c.courseId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={selectedCourseIds.includes(c.courseId)}
                        onChange={() => toggleCourse(c.courseId)}
                        disabled={isViewMode}
                      />
                      {c.courseName}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Visible From */}
            <div>
              <label style={labelStyle}>
                Visible From <span style={{ color: "var(--status-danger)" }}>*</span>
              </label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={inputStyle}
                disabled={isViewMode}
              />
            </div>

            {/* Visible To */}
            <div>
              <label style={labelStyle}>
                Visible To <span style={{ color: "var(--status-danger)" }}>*</span>
              </label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={inputStyle}
                disabled={isViewMode}
              />
            </div>
          </div>

          {/* Batch Selection — hidden for level-based issuing unless overridden */}
          {hasLevelSelected && !showBatchOverride ? (
            <div style={{ marginBottom: 20, padding: 12, background: "var(--status-success-bg)", border: "1px solid var(--status-success-border)", borderRadius: 6, fontSize: 13, color: "var(--status-success-text)" }}>
              Issues to entire {levelLabel} (all current and future batches).{" "}
              {!isViewMode && (
                <button
                  onClick={() => setShowBatchOverride(true)}
                  style={{ background: "none", border: "none", color: "var(--status-success)", cursor: "pointer", textDecoration: "underline", fontSize: 13, padding: 0 }}
                >
                  Select specific batches instead
                </button>
              )}
            </div>
          ) : (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <label style={{ fontWeight: 600, fontSize: 13 }}>
                  Batches <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>(optional)</span>
                </label>
                {showBatchOverride && hasLevelSelected && !isViewMode && (
                  <button
                    onClick={() => { setShowBatchOverride(false); setSelectedBatchIds([]); }}
                    style={{ background: "none", border: "none", color: "var(--status-success)", cursor: "pointer", textDecoration: "underline", fontSize: 13, padding: 0 }}
                  >
                    Issue to entire {levelLabel} instead
                  </button>
                )}
              </div>
              {loadingBatches ? (
                <div style={{ padding: 12, color: "var(--text-muted)", fontSize: 13 }}>Loading batches...</div>
              ) : filteredBatches.length === 0 ? (
                <div style={{ padding: 12, color: "var(--text-muted)", fontSize: 13, background: "var(--surface-page)", borderRadius: 6 }}>
                  {selectedCourseIds.length > 0 || selectedProgramId || selectedYearId
                    ? "No batches found for the selected filters. Leave empty to issue to the entire school."
                    : "Optional — pick School/Year/Course/Program to narrow to specific batches, or leave empty to issue to the entire school."}
                </div>
              ) : (
                <div style={{ border: "1px solid var(--border-strong)", borderRadius: 6, maxHeight: 200, overflowY: "auto", padding: 8 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontWeight: 600, fontSize: 13, borderBottom: "1px solid var(--border-default)", marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      checked={selectedBatchIds.length === filteredBatches.length && filteredBatches.length > 0}
                      onChange={handleSelectAllBatches}
                      disabled={isViewMode}
                    />
                    Select All ({filteredBatches.length})
                  </label>
                  {filteredBatches.map((b) => (
                    <label key={b.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={selectedBatchIds.includes(b.id)}
                        onChange={() => toggleBatch(b.id)}
                        disabled={isViewMode}
                      />
                      {b.displayName || b.name}
                      {b.batchCode && <span style={{ color: "var(--text-faint)", fontSize: 11 }}>({b.batchCode})</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notifications */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600, fontSize: 13 }}>Notifications</label>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <NotificationCheckbox label="SMS" checked={notifySms} onChange={setNotifySms} message={smsMessage} onMessageChange={setSmsMessage} disabled={isViewMode} />
              <NotificationCheckbox label="E-mail" checked={notifyEmail} onChange={setNotifyEmail} message={emailMessage} onMessageChange={setEmailMessage} disabled={isViewMode} />
              <NotificationCheckbox label="App" checked={notifyApp} onChange={setNotifyApp} message={appMessage} onMessageChange={setAppMessage} disabled={isViewMode} />
            </div>
          </div>

          {/* Submit */}
          {!isViewMode && (
            <button
              onClick={handleIssue}
              disabled={issuing || !canIssue}
              style={{
                padding: "10px 24px",
                backgroundColor: issuing || !canIssue ? "#9ca3af" : "#059669",
                color: "var(--text-on-brand)",
                border: "none",
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 14,
                cursor: issuing || !canIssue ? "not-allowed" : "pointer",
              }}
            >
              {getIssueLabel()}
            </button>
          )}
        </div>
      </div>

      {/* Issue History Card */}
      <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <span>Issue History</span>
          <button
            onClick={loadHistory}
            disabled={loadingHistory}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--status-success)" }}
          >
            {loadingHistory ? "Loading..." : "Refresh"}
          </button>
        </div>
        <div style={{ padding: 0 }}>
          {loadingHistory && issueHistory.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--text-muted)" }}>Loading issue history...</div>
          ) : issueHistory.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--text-muted)" }}>
              No issues found. Use the form above to issue this resource.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border-default)", textAlign: "left" }}>
                    <th style={thStyle}>Batch</th>
                    <th style={thStyle}>School</th>
                    {isProgramBased !== false && <th style={thStyle}>Program</th>}
                    <th style={thStyle}>Course</th>
                    <th style={thStyle}>Year</th>
                    <th style={thStyle}>Visible From</th>
                    <th style={thStyle}>Visible To</th>
                    <th style={thStyle}>Issued On</th>
                    {!isViewMode && <th style={thStyle}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {issueHistory.map((issue) => (
                    <tr key={issue.id} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                      <td style={tdStyle}>
                        {issue.batchId
                          ? (issue.batchDisplayName || issue.batchName || "-")
                          : (issue.courseId || issue.programId || issue.schoolId ? "All batches" : "-")}
                      </td>
                      <td style={tdStyle}>{issue.schoolName || "-"}</td>
                      {isProgramBased !== false && <td style={tdStyle}>{issue.programName || "-"}</td>}
                      <td style={tdStyle}>{issue.courseName || "-"}</td>
                      <td style={tdStyle}>{issue.batchId ? "—" : (issue.yearName || "All years")}</td>
                      <td style={tdStyle}>{formatDate(issue.startDate)}</td>
                      <td style={tdStyle}>{formatDate(issue.endDate)}</td>
                      <td style={tdStyle}>{formatDate(issue.creationDate)}</td>
                      {!isViewMode && (
                        <td style={tdStyle}>
                          <button
                            onClick={() => setDeleteConfirmId(issue.id)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "var(--status-danger)",
                              cursor: "pointer",
                              fontSize: 13,
                              textDecoration: "underline",
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteConfirmId !== null} onHide={() => setDeleteConfirmId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to remove this issue? The resource will no longer be visible to students.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteConfirmId(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// ── Notification checkbox sub-component ────────────────────────────────────

function NotificationCheckbox({
  label,
  checked,
  onChange,
  message,
  onMessageChange,
  disabled = false,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  message: string;
  onMessageChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ flex: "1 1 200px" }}>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
        {label}
      </label>
      {checked && (
        <textarea
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          placeholder={`${label} message (optional)`}
          rows={2}
          style={{ ...inputStyle, marginTop: 6, width: "100%", resize: "vertical" }}
          disabled={disabled}
        />
      )}
    </div>
  );
}

// ── Shared inline styles ───────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: "var(--surface-card)",
  border: "1px solid var(--border-default)",
  borderRadius: 16,
  boxShadow: "0 1px 2px rgba(0,0,0,.04)",
  overflow: "hidden",
  marginBottom: 16,
};

const cardHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "14px 18px",
  borderBottom: "1px solid var(--border-default)",
  fontWeight: 600,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: 4,
  fontWeight: 600,
  fontSize: 13,
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid var(--border-strong)",
  fontSize: 13,
  backgroundColor: "var(--surface-card)",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid var(--border-strong)",
  fontSize: 13,
};

const loadingHint: React.CSSProperties = {
  fontSize: 11,
  color: "var(--text-faint)",
  marginTop: 2,
  display: "inline-block",
};

const thStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontWeight: 600,
  fontSize: 12,
  color: "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
};
